import React, { useEffect } from 'react'
import { useApi } from '../../../hooks/useApi'
import { ApiConfig } from '../../../config/api'
import axios from 'axios'
import { ApiResponse } from '../../../typings/ApiResponse'
import { usePagination } from '../../../hooks/usePagination'
import { InventoryCheckDynamicCycleTable } from '../InventoryCheckDynamicCycleTable'
import { Pagination } from '../../../components/Pagination'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../../components/UI'
import { Box, Link } from '@mui/material'
import { SystemUpdateAlt as SystemUpdateAltIcon } from '@mui/icons-material'
import { ModalReturnType } from '../../../hooks/useModal'
import { Batch } from '../../../interfaces/Batch'
import { formatExpirationDate } from '../../../utils/dateHelper'

interface InventoryCheckResultModalProps {
  modalState: ModalReturnType<{ id: number }>
  inventoryCheckId: number
}

export interface InventoryCheckResultInfoDto {
  position: string // Código de la posición, por ejemplo "A0001"
  sku: string // SKU del artículo, por ejemplo "4064048025476"
  ean: string
  details: string
  batch?: Batch
  productOriginId: number
}

export type CycleDetail = {
  CheckedQty: number
  RealQty: number
  User?: string | null
}

export type TransformedData = {
  position: string
  sku: string
  ean: string
  batch: string
  productOriginId: number
  cycles: Record<string, CycleDetail>
  expirationDate?: string | null
}

const getInventoryCheckResult = (
  inventoryCheckId: number,
  page: number,
  pageSize: number
) => {
  const url = new URL(
    `${ApiConfig.InventoryCheckApi}/${inventoryCheckId}/results`
  )

  url.searchParams.set('page', page.toString())
  url.searchParams.set('pageSize', pageSize.toString())
  return axios.get(url.toString())
}

const getInventoryCheckFileResult = (inventoryCheckId: number) => {
  const url = new URL(
    `${ApiConfig.InventoryCheckApi}/${inventoryCheckId}/resultfile`
  )

  return axios.get(url.toString())
}

export const InventoryCheckResultModal: React.FC<
  InventoryCheckResultModalProps
> = ({ inventoryCheckId }) => {
  const { state, callApi } = useApi<ApiResponse<InventoryCheckResultInfoDto>>()

  const { state: requestFileState, callApi: requestFileCallApi } = useApi()

  const requestGetInventoryCheckResult = () => {
    callApi(
      getInventoryCheckResult(
        inventoryCheckId,
        pagination.page,
        pagination.pageSize
      )
    )
  }

  const pagination = usePagination(5)

  useEffect(() => {
    requestGetInventoryCheckResult()
  }, [pagination.page, pagination.pageSize])

  const handlePagination = (page = 0, pageSize = 5) => {
    const auxPage = pageSize === pagination.pageSize ? page : 0

    pagination.setPagination(auxPage, pageSize)
  }

  function transformData(
    initialData: InventoryCheckResultInfoDto[]
  ): TransformedData[] {
    return initialData.map((item) => {
      const cycles: Record<string, CycleDetail> = {}

      // Parse details and map each cycle
      const details = JSON.parse(item.details)
      details.forEach((detail: any) => {
        const cycleKey = detail.Cycle.toString()
        cycles[cycleKey] = {
          CheckedQty: detail.CheckedQty,
          RealQty: detail.RealQty,
          User: detail.User || null
        }
      })

      // Return transformed object
      return {
        position: item.position,
        sku: item.sku,
        ean: item.ean,
        productOriginId: item.productOriginId,
        expirationDate: item.batch?.expirationDate,
        batch:
          item.batch?.batchNumber ||
          (item.batch?.expirationDate
            ? formatExpirationDate(item.batch.expirationDate, 'dd-MM-yyyy')
            : 'Sin Lote/Expiración'),
        cycles
      }
    })
  }

  const requestGetInventoryCheckFileResult = () => {
    requestFileCallApi(getInventoryCheckFileResult(inventoryCheckId))
      .then((response: { name: string; mimeType: string; file: string }) => {
        if (response !== null) {
          const raw = window.atob(response.file)
          const uInt8Array = new Uint8Array(raw.length)
          for (let i = 0; i < raw.length; ++i) {
            uInt8Array[i] = raw.charCodeAt(i)
          }
          const link = document.createElement('a')
          const blob = new Blob([uInt8Array], {
            type: response.mimeType
          })
          link.style.display = 'none'
          link.href = URL.createObjectURL(blob)
          link.setAttribute('download', response.name)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      })
      .catch((err) => {
        console.log('error descargando el archivo', err)
      })
  }

  if (state.status === Status.Loading) {
    return <Loading />
  }

  return (
    <div>
      <InventoryCheckDynamicCycleTable
        data={transformData(state.data?.items || [])}
      />
      <Pagination
        pagination={{
          page: pagination.page || 0,
          pageSize: pagination.pageSize || 0,
          totalItem: state.data?.totalItems || 0
        }}
        onPaginationChange={handlePagination}
      />
      {requestFileState.status === Status.Loading && <Loading />}
      {requestFileState.status !== Status.Loading && (
        <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-start'}>
          <SystemUpdateAltIcon
            color='disabled'
            style={{ paddingRight: '10px', verticalAlign: 'middle' }}
          />
          <Link
            style={{ cursor: 'pointer' }}
            onClick={requestGetInventoryCheckFileResult}
          >
            Descargar resultado del Conteo
          </Link>
        </Box>
      )}
    </div>
  )
}
